import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages the cart drawer.
 *
 * Layout is a plain flex column (fixed header, scrolling middle, fixed footer) defined in
 * snippets/header-actions.liquid, so no sticky-summary measuring is needed here.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {MutationObserver | null} */
  #zeroObserver = null;
  /** @type {boolean} */
  #stripping = false;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);

    // The theme's global zero-decimal strip (layout/theme.liquid) does not reach the totals
    // inside this <dialog>: the drawer subtree is hydrated after DOMContentLoaded, so it misses
    // the one-time synchronous pass and its live MutationObserver was only attached to the
    // `.cart-totals` present at load. Strip the drawer's own totals here, synchronously (no rAF,
    // which pauses on hidden tabs), using the same locale-safe rule the rest of the site uses.
    this.#stripZeroDecimals();
    this.#zeroObserver = new MutationObserver(this.#onMutation);
    const inner = this.querySelector('.cart-drawer__inner');
    if (inner) {
      this.#zeroObserver.observe(inner, { childList: true, subtree: true, characterData: true });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.#zeroObserver?.disconnect();
    this.#zeroObserver = null;
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  #onMutation = () => {
    // Ignore the mutations our own strip produces (re-entrancy guard).
    if (this.#stripping) return;
    this.#stripZeroDecimals();
  };

  #stripZeroDecimals() {
    const values = this.querySelectorAll('.cart-totals__total-value, .cart-totals__original-value');
    if (!values.length) return;
    this.#stripping = true;
    try {
      for (const el of values) {
        const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
        let node;
        while ((node = walker.nextNode())) {
          const v = node.nodeValue;
          if (v && /[,.]00(\s|$)/.test(v)) {
            node.nodeValue = v.replace(/[,.]00(\s)/g, '$1').replace(/[,.]00$/g, '');
          }
        }
      }
    } finally {
      // Let the mutations we just made flush before re-enabling, without rAF.
      queueMicrotask(() => {
        this.#stripping = false;
      });
    }
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
